/**
 * Label/value pairs, laid out so that side-by-side placement is EARNED.
 *
 * There is no `columns` prop and no column count anywhere in this API, because
 * a column count is precisely what produced the defect this replaces: a caller
 * asked for three columns, the values were sentences, and the grid rendered
 * three unreadable slivers. Here the default is a single labelled stack at
 * reading width, and two entries pair only when BOTH are declared `Short`,
 * both render inside the short-value budget, and the surface is not Mobile. A
 * three- or four-up group exists only where the caller explicitly declares
 * `FourUp`, and only for short same-kind figures — O/H/L/V, or
 * enacted / in force / extent / modified.
 *
 * The four visual treatments are inherited from `Csegmentedpanel` so nothing
 * is lost by retiring it from this surface — but the LAYOUT decision is
 * resolved here and never handed down as a fixed grid, which is the seam that
 * produced the defect in the first place.
 *
 * A SHORT LAST ROW IS CLOSED BY ITS OWN CELL, never left as a hole. Every
 * register paints the container behind the cells and seams them against it, so
 * an empty grid slot is not blank — it is a lit plate with no label and no
 * value, which is precisely what an odd fact count drew beside `PUSHED` on the
 * github Manifest cards. The last cell spans the remainder instead. There is no
 * pad entry and no filler cell anywhere in this component: a cell exists because
 * a caller handed over a fact.
 *
 * What is NOT inherited is geometry. `CSEGMENTED_PANEL_PRESENTATION` hardcodes
 * a 10px container (13px for `InsetCells`, over 9px cells), and those numbers
 * are off the kit's ladder and larger than the surfaces that hold this group: a
 * 10px container inside a 9px core, and inside a 5px disclosure. That is the one
 * radius inversion in the kit, and SPEC §3.1 is unambiguous — the rung comes
 * from `CresultcardRadiusContext` and nowhere else. So the register supplies the
 * treatment (gaps, padding, fills, label cast) and the ladder supplies the
 * radius, which also leaves `Csegmentedpanel`'s own consumers exactly where they
 * are.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import { CSEGMENTED_PANEL_PRESENTATION } from "@/components/ui/Csegmentedpanel/csegmentedpanel.styles";
import { CsegmentedpanelVariant } from "@/components/ui/Csegmentedpanel";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
  CRC_TONE_COLOR,
  radiusBelow,
} from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import {
  CresultcardFactGroupShape,
  CresultcardFactWeight,
  CresultcardTone,
  type CresultcardFactEntry,
} from "./cresultcard.types";

/**
 * The short-value budget, in characters. Past it a "short" value is a sentence
 * that has not admitted it yet, and pairing it produces the sliver.
 */
const SHORT_VALUE_BUDGET = 18;
const FOUR_UP_COLUMNS = 4;
const PAIR_COLUMNS = 2;
const SINGLE_COLUMN = 1;
const ONE_ENTRY = 1;
/** A last row that is exactly full: nothing for the last cell to cover. */
const NO_REMAINDER = 0;

interface CresultcardFactGroupProps {
  readonly entries: readonly CresultcardFactEntry[];
  /** The only layout declaration available, and only for same-kind figures. */
  readonly group?: CresultcardFactGroupShape;
  readonly register?: CsegmentedpanelVariant;
}

/**
 * Whether this entry may share a line, which only a MEASURABLE value can earn.
 *
 * A character budget can only be applied to characters. `String(value)` on a
 * composed node yields `[object Object]` — fifteen characters, inside the
 * budget — so every `ReactElement` value declared `Short` passed automatically,
 * and two of them paired into the exact slivers the missing `columns` prop
 * exists to prevent. A composed value is a value whose width this component
 * cannot know, so it does not earn the pair.
 */
function isShort(entry: CresultcardFactEntry): boolean {
  if (entry.weight !== CresultcardFactWeight.Short) {
    return false;
  }
  const { value } = entry;
  if (typeof value === "number") {
    return true;
  }
  return typeof value === "string" && value.length <= SHORT_VALUE_BUDGET;
}

interface ResolveColumnsInput {
  readonly entries: readonly CresultcardFactEntry[];
  readonly group: CresultcardFactGroupShape;
  readonly isMobile: boolean;
}

/** Mobile never forces two columns; a declared four-up is honoured at Wide. */
function resolveColumns({
  entries,
  group,
  isMobile,
}: ResolveColumnsInput): number {
  if (isMobile || group === CresultcardFactGroupShape.Column) {
    return SINGLE_COLUMN;
  }
  if (group === CresultcardFactGroupShape.FourUp) {
    return Math.min(FOUR_UP_COLUMNS, entries.length);
  }
  return entries.every(isShort) && entries.length >= PAIR_COLUMNS
    ? PAIR_COLUMNS
    : SINGLE_COLUMN;
}

export function CresultcardFactGroup({
  entries,
  group = CresultcardFactGroupShape.Stack,
  register = CsegmentedpanelVariant.Seamed,
}: CresultcardFactGroupProps): ReactElement | null {
  const { isMobile } = useCresultcardViewport();
  const radius = useCresultcardNestedRadius();

  if (entries.length === 0) {
    return null;
  }

  const presentation = CSEGMENTED_PANEL_PRESENTATION[register];
  const columns = resolveColumns({ entries, group, isMobile });
  const lastIndex = entries.length - ONE_ENTRY;
  /* How many columns the LAST cell covers so its row closes with no bare plate.
   * With `r` entries on a short last row, the first `r − 1` take one column each
   * and the last takes what is left: `columns − r + 1`. A last row that is full
   * has no remainder and the cell keeps its own single column. */
  const lastRowFill = entries.length % columns;
  const lastCellSpan =
    lastRowFill === NO_REMAINDER
      ? undefined
      : `span ${String(columns - lastRowFill + SINGLE_COLUMN)}`;
  /**
   * A cell rung only where the register actually paints a cell surface —
   * `InsetCells` is the one that does. The other three seam their cells against
   * a shared background, and a radius on a cell with no surface of its own is a
   * rounded corner nothing draws.
   */
  const cellRadius =
    presentation.cell.borderRadius == null ? undefined : radiusBelow(radius);
  const labelStyle: CSSProperties = {
    fontSize: 8.5,
    fontWeight: 640,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    lineHeight: 1,
    color: CRC_TEXT_FAINT,
    ...presentation.label,
  };

  return (
    <div
      data-el="Cresultcard/FactGroup/Root"
      data-crc-register={register}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${String(columns)}, minmax(0, 1fr))`,
        minWidth: 0,
        ...presentation.container,
        borderRadius: radius,
      }}
    >
      {entries.map((entry, index) => (
        /* PATHED by the entry's own label, because every cell answering to one
         * `/Cell` path is a measurement that cannot be taken: an 85px overflow
         * was read off the Rate cell and belonged to Nearby, and nothing in the
         * DOM could tell the two apart. The label is what the reader sees, so it
         * is also what a measurement should name.
         *
         * KEYED by the label AND the position, which is a different question.
         * A label is a reader-facing word and callers legitimately repeat it —
         * trends prints the reading's own key on every member of a share row, so
         * one card renders three `share` cells — and React needs each sibling
         * distinguishable, not each readable. The label alone raised eight
         * duplicate-key errors per render on that surface. Position is the
         * missing half: the list is ordered and never reordered in place, so the
         * pair is stable across updates. */
        <div
          data-el="Cresultcard/FactGroup/Entry"
          key={`${entry.label}-${String(index)}`}
          style={{
            ...presentation.cell,
            borderRadius: cellRadius,
            /* The pad. A grid with more columns than the last row has entries
             * leaves that remainder EMPTY — and the container paints a lighter
             * seam behind its cells, so the gap arrives as a filled plate with
             * no label and no value beside the last real cell (every github
             * Manifest card with an odd fact count). The last entry spans the
             * remainder instead: the row is closed by a cell rather than by a
             * hole, and a group that fills its last row is unaffected. */
            gridColumn: index === lastIndex ? lastCellSpan : undefined,
          }}
        >
          <span
            style={labelStyle}
          >
            {entry.label}
          </span>
          <span
            style={{
              ...CRC_NUMERIC_STYLE,
              fontSize: 11.5,
              fontWeight: 640,
              lineHeight: 1.3,
              overflowWrap: "anywhere",
              color: CRC_TONE_COLOR[entry.tone ?? CresultcardTone.Neutral],
              ...presentation.value,
            }}
          >
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}
